import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  CreditCard, Coins, Receipt, FileText, ExternalLink, 
  Crown, Gift, ChevronRight, Loader2, Download, Calendar,
  Shield, CheckCircle
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

import { BACKEND_URL } from '../config/api';

// ==================== CREDIT PURCHASE MODAL ====================
export function CreditPurchaseModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  packages = [],
  isFoundingEmployer = false,
  founderBonusPercent = 20
}) {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingPackages, setFetchingPackages] = useState(false);
  const [localPackages, setLocalPackages] = useState(packages);
  const [error, setError] = useState(null);

  // Sync localPackages with incoming packages prop
  useEffect(() => {
    if (packages && packages.length > 0) {
      setLocalPackages(packages);
      setFetchingPackages(false);
    }
  }, [packages]);

  // Fetch packages if not provided or empty after modal opens
  useEffect(() => {
    if (isOpen && localPackages.length === 0 && !fetchingPackages) {
      setFetchingPackages(true);
      setError(null);
      axios.get(`${BACKEND_URL}/api/credits/settings`, { withCredentials: true })
        .then(response => {
          const pkgs = response.data?.topup_packages || [];
          setLocalPackages(pkgs);
        })
        .catch(err => {
          console.error('Failed to load credit packages:', err);
          setError('Unable to load credit packages. Please try again.');
        })
        .finally(() => {
          setFetchingPackages(false);
        });
    }
  }, [isOpen, localPackages.length, fetchingPackages]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedPackage(null);
      setError(null);
    }
  }, [isOpen]);

  const handlePurchase = async () => {
    if (selectedPackage === null) {
      toast.error('Please select a package');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/credits/purchase`,
        {
          package_index: selectedPackage,
          host_url: window.location.origin
        },
        { withCredentials: true }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      // Subscription-required gate returns 403 with structured detail.
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403 && detail?.error === 'SUBSCRIPTION_REQUIRED') {
        toast.error(detail.message || 'Subscribe before buying credit top-ups');
        onClose?.();
        window.location.href = detail.redirect || '/';
        return;
      }
      toast.error(detail?.message || detail || 'Failed to initiate purchase');
      setLoading(false);
    }
  };

  const displayPackages = localPackages.length > 0 ? localPackages : packages;
  const enabledPackages = displayPackages.filter(pkg => pkg.enabled !== false);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-orange-500" />
            Buy Credits
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {/* Founding Employer Bonus Banner */}
          {isFoundingEmployer && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center gap-3">
              <Crown className="h-5 w-5 text-amber-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">Founding Employer Bonus</p>
                <p className="text-xs text-amber-600">You get {founderBonusPercent}% extra credits on every purchase!</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {fetchingPackages && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 text-orange-500 animate-spin" />
              <span className="ml-2 text-slate-600">Loading credit packages...</span>
            </div>
          )}

          {/* Error State */}
          {error && !fetchingPackages && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  setFetchingPackages(false);
                }}
                className="text-sm text-red-600 underline mt-2"
              >
                Try again
              </button>
            </div>
          )}

          {/* No Packages Available */}
          {!fetchingPackages && !error && enabledPackages.length === 0 && (
            <div className="text-center py-8">
              <Coins className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">No credit packages available at the moment.</p>
              <p className="text-sm text-slate-500 mt-1">Please try again later or contact support.</p>
            </div>
          )}

          {/* Package Selection */}
          {!fetchingPackages && !error && enabledPackages.length > 0 && (
            <>
              <div className="space-y-3 mb-6">
                {enabledPackages.map((pkg, index) => {
                  const bonusCredits = isFoundingEmployer ? Math.floor(pkg.credits * founderBonusPercent / 100) : 0;
                  const totalCredits = pkg.credits + bonusCredits;
                  
                  return (
                    <button
                      key={pkg.key || pkg.id || `pkg-${index}`}
                      onClick={() => setSelectedPackage(index)}
                      disabled={loading}
                      className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                        selectedPackage === index
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      data-testid={`package-${index}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-2xl font-bold text-slate-900">{pkg.credits}</span>
                            <span className="text-slate-500">credits</span>
                            {bonusCredits > 0 && (
                              <span className="bg-amber-100 text-amber-700 text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Gift className="h-3 w-3" />
                                +{bonusCredits} bonus
                              </span>
                            )}
                            {pkg.popular && (
                              <span className="bg-orange-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                                Popular
                              </span>
                            )}
                          </div>
                          {pkg.savings && (
                            <p className="text-xs text-green-600 font-medium mt-1">Save {pkg.savings}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-slate-900">£{pkg.price?.toFixed(2) || '0.00'}</p>
                          {bonusCredits > 0 && (
                            <p className="text-xs text-amber-600">{totalCredits} total</p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Purchase Button */}
              <button
                onClick={handlePurchase}
                disabled={loading || selectedPackage === null}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="purchase-credits-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4" />
                    Continue to Payment
                  </>
                )}
              </button>
            </>
          )}

          {/* Secure Payment Note */}
          <p className="text-center text-xs text-slate-500 mt-3 flex items-center justify-center gap-1">
            <Shield className="h-3 w-3" />
            Secure payment powered by Stripe
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ==================== BILLING MANAGEMENT COMPONENT ====================
export default function BillingManagement({ user }) {
  const [loading, setLoading] = useState(false);
  const [billingHistory, setBillingHistory] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      const [historyRes, invoicesRes, methodsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/billing/history`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/billing/invoices`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/billing/payment-methods`, { withCredentials: true })
      ]);
      
      setBillingHistory(historyRes.data.payments || []);
      setInvoices(invoicesRes.data.invoices || []);
      setPaymentMethods(methodsRes.data.payment_methods || []);
    } catch (error) {
      console.error('Failed to fetch billing data');
    }
  };

  const openCustomerPortal = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/billing/portal`,
        { host_url: window.location.origin },
        { withCredentials: true }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to open billing portal');
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="billing-management">
      {/* Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-orange-500" />
            <h3 className="font-semibold text-slate-900">Billing & Payments</h3>
          </div>
          <button
            onClick={openCustomerPortal}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 transition-colors disabled:opacity-50"
            data-testid="manage-billing-btn"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ExternalLink className="h-4 w-4" />
            )}
            Manage Billing
          </button>
        </div>
      </div>

      {/* Payment Methods */}
      {paymentMethods.length > 0 && (
        <div className="p-4 border-b border-slate-100">
          <p className="text-sm text-slate-500 mb-2">Saved Payment Methods</p>
          <div className="space-y-2">
            {paymentMethods.map((method) => (
              <div key={method.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-10 h-6 bg-gradient-to-r from-slate-700 to-slate-800 rounded flex items-center justify-center">
                  <span className="text-white text-[8px] font-bold uppercase">{method.brand}</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">•••• {method.last4}</p>
                  <p className="text-xs text-slate-500">Expires {method.exp_month}/{method.exp_year}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-slate-500">Recent Transactions</p>
          {billingHistory.length > 3 && (
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="text-xs text-orange-600 hover:text-orange-700 font-medium"
            >
              {showHistory ? 'Show Less' : 'View All'}
            </button>
          )}
        </div>

        {billingHistory.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No transactions yet</p>
        ) : (
          <div className="space-y-2">
            {billingHistory.slice(0, showHistory ? undefined : 3).map((txn) => (
              <div 
                key={txn.transaction_id} 
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    txn.type === 'credit_topup' ? 'bg-green-100' : 'bg-blue-100'
                  }`}>
                    {txn.type === 'credit_topup' ? (
                      <Coins className="h-4 w-4 text-green-600" />
                    ) : (
                      <Receipt className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      {txn.type === 'credit_topup' 
                        ? `${txn.total_credits || txn.credits} Credits` 
                        : txn.plan_type || 'Subscription'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(txn.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-900">£{txn.amount.toFixed(2)}</p>
                  <p className={`text-xs font-medium ${
                    txn.payment_status === 'paid' ? 'text-green-600' : 'text-amber-600'
                  }`}>
                    {txn.payment_status === 'paid' ? 'Paid' : 'Pending'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invoices */}
      {invoices.length > 0 && (
        <div className="p-4 border-t border-slate-100">
          <p className="text-sm text-slate-500 mb-3">Invoices</p>
          <div className="space-y-2">
            {invoices.slice(0, 5).map((invoice) => (
              <div 
                key={invoice.id}
                className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-700">{invoice.number || 'Invoice'}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(invoice.created).toLocaleDateString()} • £{invoice.amount.toFixed(2)}
                    </p>
                  </div>
                </div>
                {invoice.invoice_pdf && (
                  <a
                    href={invoice.invoice_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    title="Download PDF"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== CREDIT PURCHASE SUCCESS HANDLER ====================
export function CreditPurchaseSuccess({ sessionId, onComplete }) {
  const [status, setStatus] = useState('checking');
  const [credits, setCredits] = useState(0);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollStatus();
    }
  }, [sessionId]);

  const pollStatus = async () => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/credits/purchase/status/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setCredits(response.data.transaction?.credits || 0);
        if (onComplete) {
          setTimeout(onComplete, 2000);
        }
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Keep polling
        setAttempts(a => a + 1);
        setTimeout(pollStatus, 2000);
      }
    } catch (error) {
      setStatus('error');
    }
  };

  if (status === 'checking') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-sm text-center">
          <Loader2 className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Processing Payment</h3>
          <p className="text-slate-500">Please wait while we confirm your purchase...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-sm text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Purchase Complete!</h3>
          <p className="text-slate-500 mb-4">{credits} credits have been added to your account.</p>
          <div className="text-xs text-slate-400">Redirecting...</div>
        </div>
      </div>
    );
  }

  return null;
}
